import {bridgeCallLog} from '@/utils/log'
import getUaParam, {getSearchParams, setLoginUrl} from '@/utils/getUaParam'
import {IEventName, getEventId} from '@/constants/haboEventId'
import {BAR_STATUS, BAR_STATUS_NAME, IOS_GESTURE_STATUS, IOS_GESTURE_STATUS_NAME, SHARE_CHANNEL, SHARE_CHANNEL_CODE, SHARE_CHANNEL_NAME} from '@/constants/enum'
import {deBounceImmediately, isVersionHigherThan} from '@/utils'
import {IS_CLIENT} from '@/constants/env'
import {NO_ANIMATION_VERSION} from '@/constants/app'
import {APP_CONFIG_MAP} from './initOpenAppSDK'

const {
    isApp,
    isIOS,
    isAndroid,
} = getUaParam()
const {
    callNative,
    asyncCallNative,
    registerCallback,
} = IS_CLIENT
    ? require('@gaotu/js-bridge').default
    : {callNative() {}, asyncCallNative() {}, registerCallback() {}}

// 端能力链接: https://app-tech.baijia.com/jsbridge

/**
 * @description: 调用bridge的包裹函数
 * @param apiName: 名称
 * @param params: 参数
 */
const callBridge = (apiName: string, params?: IObject, callback?:(data:any)=>void) => {
    if (!isApp) {
        return
    }
    bridgeCallLog(apiName, params)
    callNative({apiName, params, callback})
}

/**
 * @description: 显示导航栏和状态栏
 */
export const showNavigationStatusBar = () => callBridge('handleNavigationStatusBar', {type: 1})

/**
 * @description: 隐藏导航栏和状态栏(只是不占空间, 状态栏仍然fixed在最上图层)
 */
export const hideNavigationStatusBar = () => callBridge('handleNavigationStatusBar', {type: 2})

/**
 * @description: 隐藏导航栏和状态栏(完全隐藏, 状态栏也消失)
 */
export const hideNavigationStatusBarCompletely = () => callBridge('hideStatusBar', {type: 1})

/**
 * @description: 显示导航栏和状态栏(完全隐藏, 状态栏也消失)
 */
export const showNavigationStatusBarCompletely = () => callBridge('hideStatusBar', {type: 2})

/**
 * @description: 打开关闭ios手势(h5弹窗使用, 避免手势返回直接退出页面)
 * @param webEnable: 枚举 IOS_GESTURE_STATUS
 * @param pageEnable: 枚举 IOS_GESTURE_STATUS
 */
export const enableIOSGesture = (webEnable: IOS_GESTURE_STATUS_NAME, pageEnable: IOS_GESTURE_STATUS_NAME) => {
    if (!isIOS) {
        return
    }
    callBridge('enableIOSBackForwardNavigationGestures', {webEnable: IOS_GESTURE_STATUS[webEnable], pageEnable: IOS_GESTURE_STATUS[pageEnable]})
}

/**
 * @description: 解除客户端的所有限制(各种失败条件下需要调用这个端能力, 避免app异常)
 */
export const removeAllRestrictions = () => {
    // 打开IOS手势
    enableIOSGesture('enabled', 'enabled')
    // 显示IOS的statusBar
    showNavigationStatusBarCompletely()
}

/**
 * @description: 返回上一级页面, 没有上一级就退出当前窗口
 */
export const closeWindow = () => {
    removeAllRestrictions()
    callBridge('closeWindow')
}

/**
 * @description: 直接退出webview
 */
export const closeWebview = () => {
    removeAllRestrictions()
    // @ts-ignore
    callBridge('closeWebView', '1')
}

/**
 * @description: 查看pdf、excel等
 * @param name: 打开内容的标题
 * @param url: 打开内容的链接
 */
export const openMaterial = (name: string, url: string) => callBridge('openMaterial', {name, url})

/**
 * @description: 跳转到登录页
 * @param callback: 登录成功的回调()
 */
export const toLoginPage = async (callback?: (data?: any) => void) => new Promise(async (resolve) => {
    if (!isApp) {
        return
    }
    // 登录摄者sid后需要重新获取, 不能取全局的
    if (getUaParam().sid) {
        callback?.()
        return
    }
    const customCallback = (data: any) => {
        console.log('login success ===> ', data)
        setLoginUrl({...getSearchParams(), ...data || {}})
        callback?.(data)
        resolve(data)
    }
    if (isAndroid) {
        registerCallback('nativeLoginCallback', customCallback)
    }
    bridgeCallLog('userLogin', {
        useCallback: '1',
        redirectUrl: location.href, // 项目部署的地址
    })
    // useCallback - 登录后通知原页面还是跳转页面: "0"-跳转 "1"-通知页面
    // redirectUrl - 登录后要跳转的页面
    // useCallback是1的时候也要写redirectUrl, 否则bridge会报错
    const res = await asyncCallNative({
        apiName: 'userLogin',
        params: {
            useCallback: '1',
            redirectUrl: location.href,
            channel: 'tushu_online_service',
        },
    })
    customCallback(res.data)
})

/**
 * @description: 跳转到切换账号页
 * @param callback: 切换成功的回调()
 */
export const toSwitchAccountPage = async (callback?: (data?: any) => void) => {
    if (!isApp) {
        return
    }
    const customCallback = (data: any) => {
        console.log('switch account success ===> ', data)
        setLoginUrl({...getSearchParams(), ...data || {}})
        callback?.(data)
    }
    if (isAndroid) {
        registerCallback('nativeLoginCallback', customCallback)
    }
    bridgeCallLog('switchAccounts', {useCallback: '1', redirectUrl: location.href})
    // useCallback - 切换后通知原页面还是跳转页面: "0"-跳转 "1"-通知页面
    // redirectUrl - 切换后要跳转的页面
    // useCallback是1的时候也要写redirectUrl, 否则bridge会报错
    const res = await asyncCallNative({
        apiName: 'switchAccounts',
        params: {useCallback: '1', redirectUrl: location.href},
    })
    customCallback(res.data)
}

/**
 * @description: 分享到微信好友或朋友圈
 * @param type: 分享的类型(枚举 SHARE_CHANNEL)
 * @param title: 标题
 * @param content: 内容
 * @param img: 封面url
 * @param url: 跳转url
 */
export const shareUrlToWechat = ({
    type,
    ...params
}: {
    type: SHARE_CHANNEL_NAME;
    title: string;
    content: string;
    img: string;
    url: string;
}) => callBridge('shareUrlToWechat', {...params, type: SHARE_CHANNEL[type]})

/**
 * @description: 复制链接到剪切板
 * @param content: 复制的内容
 */
export const copyStr = (content: string) => callBridge('copyStr', {content})

/**
 * @description: 打开新的h5页面
 * @param showNavigationBar: 隐藏导航栏(枚举 BAR_STATUS)
 * @param showStatusBar: 隐藏状态栏(枚举 BAR_STATUS)
 */
export const openWindow = deBounceImmediately((
    url: string,
    navigationBarStatus: BAR_STATUS_NAME = 'hide',
    statusBarStatus: BAR_STATUS_NAME = 'hide'
) => {
    // 不在app内单独处理
    if (!isApp) {
        // 不在端内调用时直接跳转该链接
        location.href = url
        return
    }
    callBridge('openWindow', {
        url,
        showNavigationBar: BAR_STATUS[navigationBarStatus],
        showStatusBar: BAR_STATUS[statusBarStatus],
        isNoah: 1,
    })
})

/**
 * @description: 关闭当前页面, 打开新的h5或native页面, 关闭时没有动画, 必须在NO_ANIMATION_VERSION(4.78.0)之后才能使用
 */
export const openScheme = deBounceImmediately((
    url: string,
) => {
    if (!isApp) {
        location.href = url
        return
    }
    const isH5 = url.startsWith('http')
    const isNative = url.startsWith('super')
    const {appName} = getUaParam()
    if (isVersionHigherThan(NO_ANIMATION_VERSION)) {
        let link = url
        if (isH5) {
            const {scheme} = APP_CONFIG_MAP[appName as AppName]
            link = `${scheme}://jump?category=web&link=${encodeURIComponent(url)}`
        }
        callBridge('openScheme', {jumpToUrl: link, isNeedCloseTopPage: true})
        return
    }
    if (isH5) {
        closeWebview()
        openWindow(url)
    }
    if (isNative) {
        location.href = url
        closeWebview()
    }
})

/**
 * @description: 提示更新 App
 */
export const showUpgradeApp = () => callBridge('showUpgradeApp')

/**
 * @description: ios时长上报(ios退出页面前的时长报不上去)
 * @param {keyof} eventName: 事件的名称
 * @param {IObject} other: 事件的额外参数
 */
export const timeReportIOS = (eventName: IEventName, other?: IObject) => {
    if (!isIOS) {
        return
    }
    callBridge('notifyPage', {
        webPageView: {
            event_id: getEventId(eventName),
            timeKey: 'stay_time',
            timestamp: Date.now(),
            haboData: other || {placeholder: ''},
        },
    })
}

/**
 * @description: 通知native更新缓存的模版
 */
export const notifyPage = () => callBridge('notifyPage', {
    updateCache: {updateCurPage: '1'},
})

/**
 * @description: 通知native更新缓存的模版
 */
export const notifyPageFn = (params:IObject) => callBridge('notifyPage', {
    updateCache: {updateCurPage: '1', ...params},
})

/**
 * @description: 关闭音频浮窗
 */
export const closeAudioFloatWindow = () => callBridge('closeAudioFloatWindow')

/**
 * @description: 通知native取消app loading
 */
export const cancelLoading = () => callBridge('notifyPage', {
    loading: {show: '0'},
})

/**
 * @description: 获取公参
 */
export const resetUserCommonInfo = async () => {
    if (!isApp) {
        return
    }
    bridgeCallLog('getUserCommonInfo',)
    const res = await asyncCallNative({apiName: 'getUserCommonInfo'})
    if (res?.code === 0 && Object.values(res.data || {}).length) {
        setLoginUrl(res.data)
    }
}
/*
 * @description: 通知native 弹起扫码做题结果页 反馈弹窗
 * @param problemScene: 问题场景，1-拍照批改，2-写作题批改，3-口算批改（待后端确认），不传默认1
 * @param correctionId: 批改ID，不传调用失败
 * @param defaultFeedbackType: 默认选中的反馈原因  [{"type":5,"name":"题目不正确"},{"type":6,"name":"解析不正确"}]
 */
export const showCorrectFeedbackAlert = (params: IObject | undefined) => callBridge('showCorrectFeedbackAlert', params)

// 分句点评 app端通知h5跳转对应索引
export const sentencesIndexChangeCallback = (callback?: (data?: any) => void) => {
    if (!isApp) {
        return
    }
    registerCallback('sentencesIndexChangeCallback', callback)
}
// 分句点评 h5通知app跳转对应句子
export const sentencesIndexChange = (params:{action:'correct', index:number}, callback?: (data?: any) => void) => {
    callBridge('getAppParam', params, callback)
}

// 分享图片
export const shareImageToNative = (params: { type: number, url: string }) => callBridge('shareImageToNative', params)

// app upload image
export const getImageInNative = (callback: ((data: any) => void) | undefined) => callBridge('showUpLoadImage', {}, callback)
